import React from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Spin, message, Popconfirm, Modal } from 'antd';
import { ajax } from '../utils/request';
import './chat.css'; 
import '../work/index.css';
import { useTranslation } from "react-i18next"
import { getToken } from "../utils/storage";
import { marked } from 'marked'; 

import NewChatButton from "../components/newchatbutton";
import PDFReader from "../components/pdfreader";
import { MsgSVG, Trash, Send } from "../components/icons";
import FAQ from "../components/faq";

const HamburgerIcon = ({ open }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    {open ? (
      <path d="M3 6H21M3 12H21M3 18H21" stroke="black" strokeWidth="2" strokeLinecap="round"/>
    ) : (
      <path d="M3 6H21M3 12H21M3 18H21" stroke="black" strokeWidth="2" strokeLinecap="round"/>
    )}
  </svg>
);

export default function ChatPdf() {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = React.useState(false);
  const [sidebarLoading, setSidebarLoading] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [question, setQuestion] = React.useState('');
  const [file, setFile] = React.useState(null);
  const { confirm } = Modal;
  const [messages, setMessages] = React.useState([]);
  const [chats, setChats] = React.useState([]);
  const [pdfUrl, setPdfUrl] = React.useState(null);
  const [followup, setFollowup] = React.useState('');
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const chatScrollRef = React.useRef(null);
  const [count, setCount] = React.useState(1);
  const [userData, setUserData] = React.useState(null);
  const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);

  const textAreaRef = React.useRef(null);
  const [faqOpen, setFaqOpen] = React.useState(false);

  // Function to render markdown messages, from chat.js
  const getRenderedMessage = (messageContent) => {
    // Add a check for null/undefined to be safe
    if (typeof messageContent !== 'string') {
      return ''; 
    }
    return marked.parse(messageContent);
  };

  // Auto-resize textarea logic from chat.js
  const resizeTextArea = React.useCallback(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "auto";
      textAreaRef.current.style.height = textAreaRef.current.scrollHeight + "px";
      if (textAreaRef.current.scrollHeight >= 230) {
        textAreaRef.current.style.overflowY = "overlay";
      } else {
        textAreaRef.current.style.overflowY = "hidden";
      }
    }
  }, []);

  // Scroll to bottom, from chat.js
  React.useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTo({
        top: chatScrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages]);

  // Handle textarea resize, from chat.js (adapted dependency)
  const currentInput = chatId ? followup : question;
  React.useEffect(resizeTextArea, [currentInput, resizeTextArea]);

  const fetchChats = React.useCallback(async () => {
    const token = await getToken();
    if (!token) {
      return; 
    }
    try {
      setSidebarLoading(true); 
      const res = await ajax.get('/pdf/chats');
      setChats(res?.data?.data || []);
    } catch (e) {
      console.error(e);
      messageApi.error("Failed to load PDF chat history.");
    } finally {
      setSidebarLoading(false);
    }
  }, [messageApi]);

  const fetchChatById = React.useCallback(async (id) => {
    if (!id) {
      setMessages([]);
      setPdfUrl(null);
      return;
    };
    try {
      setLoading(true);
      const res = await ajax.get(`/pdf/chats/${id}`);
      const data = res?.data?.data;
      setMessages((data?.messages || []).map(m => ({
        role: m.role === 'user' ? 'user' : 'ai',
        content: m.content, // State uses 'content'
      })));
      setPdfUrl(data?.pdf?.gcs_url || null);
    } catch {
      messageApi.error('Unable to load this PDF chat.');
      navigate('/pdf/start');
    } finally {
      setLoading(false);
    }
  }, [messageApi, navigate]);

  React.useEffect(() => { fetchChats(); }, [fetchChats]);
  React.useEffect(() => { fetchChatById(chatId); }, [chatId, fetchChatById]);
  
  // User auth effect
  React.useEffect(() => {
    getToken()
      .then((token) => {
        if (token) {
          ajax.get(`/user/me`)
            .then(res => {
              let { data } = res.data;
              setUserData(data); 
            })
            .catch(error => {
              if (error.response && error.response.status === 403) {
                showConfirm();
              }
            });
        } else {
          showConfirm();
        }
      });
  }, [count]); 

  // Window resize effect
  React.useEffect(() => {
    const onResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onChooseFile = (e) => {
    const f = e.target.files?.[0];
    if (f && f.type !== 'application/pdf') {
      messageApi.warning('Please select a PDF file.');
      return;
    }
    if (f && f.size > 50 * 1024 * 1024) { // 50MB limit
      messageApi.warning('File is too large (max 25MB).');
      return;
    }
    setFile(f || null);
  };

  const startPdfConversation = async () => {
    if (!file) return messageApi.warning('Please choose a PDF file.');
    const questionToAsk = question.trim();
    if (!questionToAsk) return messageApi.warning('Please ask a question.');
    try {
      setSending(true);
      // Add user message immediately
      setMessages([{ role: 'user', content: questionToAsk }]);
      setQuestion(''); // Clear input

      const fd = new FormData();
      fd.append('file', file);
      fd.append('question', questionToAsk);
      const res = await ajax.post('/pdf/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { chat_id, answer, pdf_url } = res?.data?.data || res?.data || {};
      
      // Replace temp user message with full context
      if (answer) setMessages([
        { role: 'user', content: questionToAsk },
        { role: 'ai', content: answer }
      ]);
      if (pdf_url) setPdfUrl(pdf_url);
      setFile(null);
      await fetchChats();
      if (chat_id) navigate(`/pdf/${chat_id}`);
    } catch (e) {
      console.error(e);
      messageApi.error(e?.response?.data?.detail || 'Upload failed');
      setMessages([]); // Clear messages on failure
    } finally {
      setSending(false);
    }
  };

  // Fixed 'prev' scope
  const sendFollowup = async () => {
    if (!chatId) return messageApi.warning('Please open a PDF chat.');
    const questionToAsk = followup.trim();
    if (!questionToAsk) return;

    let prev; // Defined here

    try {
      setSending(true);
      prev = [...messages, { role: 'user', content: questionToAsk }]; // Assigned here
      setMessages(prev);
      setFollowup(''); 
      const res = await ajax.post('/pdf/chat', { chat_id: Number(chatId), question: questionToAsk });
      const { answer, pdf_url } = res?.data?.data || res?.data || {};
      if (answer) setMessages([...prev, { role: 'ai', content: answer }]);
      if (pdf_url) setPdfUrl(pdf_url); 
    } catch (e) {
      console.error(e);
      messageApi.error(e?.response?.data?.detail || 'Failed to send message.');
      if (prev) { 
        setMessages(prev); // Accessible here
      }
    } finally {
      setSending(false);
    }
  };
  
  const sendMessage = () => {
    if (chatId) {
      sendFollowup();
    } else {
      startPdfConversation();
    }
  };

  const handleEnterKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !sending) {
      e.preventDefault();
      sendMessage();
    }
  };

  const showConfirm = () => {
    confirm({
      title: t("book.tipTitle"),
      content: t("book.tipContent"),
      onOk() { window.location.href = "/"; },
      onCancel() { window.location.href = "/"; },
    });
  };

  const handleDeleteChat = async (id) => {
    try {
      await ajax.delete(`/pdf/chats/${id}`);
      messageApi.success('Chat deleted');
      if (String(id) === String(chatId)) {
        navigate('/pdf/start');
        setMessages([]);
        setPdfUrl(null);
      }
      fetchChats();
    } catch (e) {
      console.error(e);
      messageApi.error(e?.response?.data?.detail || 'Deletion failed');
    }
  };

  const handleNewChat = () => navigate('/pdf/start');
  
  // This variable controls the welcome overlay
  const showOverlay = !chatId; // Simplified: show overlay only on /pdf/start

  return (
    <div className="main h-full w-full">
      {contextHolder}

      <div className="wrapper flex flex-row">
        <FAQ isOpen={faqOpen} setOpen={setFaqOpen} />

        {/* SIDEBAR */}
        <div
          className={`sider flex flex-col bg-[#f5f5f7] transition-all duration-300`}
          style={{ width: sidebarOpen ? "256px" : "60px", height: windowHeight }}
        >
          {/* Header */}
          <div className="sider-header flex items-center justify-between mx-[8px] my-[8px]">
            {sidebarOpen && (
              <a href="/" className="sider-title flex items-center gap-2 font-bold text-black hover:opacity-80 transition-opacity">
                <img src="/images/Logo CiviDocs.png" alt="CiviDocs" className="h-[45px]" />
              </a>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="toggle-sidebar p-2 rounded-md hover:bg-gray-200"
            >
              <HamburgerIcon open={sidebarOpen} />
            </button>
          </div>

          {/* NewChatButton */}
          {sidebarOpen ? (
            <NewChatButton onNewChat={handleNewChat} />
          ) : (
            <div className="flex justify-center mx-[8px] mb-[8px]">
              <button
                onClick={handleNewChat}
                className="p-2 rounded-md hover:bg-gray-200 transition-colors"
                title="New Chat"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>
          )}

          {/* Chat List */}
          {sidebarOpen && (
            <div className="sider-scroll-area">
              <div className="pdf-list flex flex-col my-[8px]">
                {chats && chats.map((item) => {
                  const isActive = String(item.id) === String(chatId);
                  const chatDate = new Date(item.update_time).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  });

                  return (
                    <Link
                      key={item.id}
                      to={`/pdf/${item.id}`}
                      className={`${isActive ? "active" : ""} chat-item rounded-md text-[14px] flex flex-row items-center h-[40px] flex-grow group`}
                    >
                      <span>
                        <MsgSVG fill={`${isActive ? '#fff' : "#000"}`} size="18" />
                      </span>
                      <span className="whitespace-nowrap text-ellipsis overflow-hidden ml-[5px]">
                        {item.title}
                      </span>
                      <span className="ml-auto text-[12px] opacity-70">
                        {chatDate}
                      </span>
                      <Popconfirm
                        title="Delete the chat"
                        description="Are you sure to delete this chat?"
                        onConfirm={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteChat(item.id);
                        }}
                        okText="Yes"
                        cancelText="No"
                      >
                        <button
                          className="delete-chat-button opacity-0 group-hover:opacity-100 transition-opacity ml-2"
                          style={{ background: 'none', border: 'none', padding: 0 }}
                        >
                          <Trash size={18} fill="#969696" />
                        </button>
                      </Popconfirm>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Footer */}
          {sidebarOpen ? (
            <div className="sider-footer text-[#969696] flex gap-[9px] px-[8px] text-[14px] hover:text-[var(--blue-text)] cursor-pointer">
              <a className="text-[#969696] text-[14px]" href="/">
                {t("index")}
              </a>
              <div
                className="text-[#969696] hover:text-[var(--blue-text)] transition cursor-pointer"
                onClick={() => setFaqOpen(true)}
              >
                FAQ
              </div>
            </div>
          ) : (
            <div className="sider-footer-closed flex justify-center items-center pb-3">
              <a href="/" className="sidebar-mini-logo">
                <img src="/images/cividocs.png" alt="CiviBOT" className="h-[40px] w-auto opacity-80 hover:opacity-100 transition" />
              </a>
            </div>
          )}
        </div>

        {/* CONTENT */}
        <div
          className="content flex flex-row transition-all duration-300"
          style={{ width: sidebarOpen ? "calc(100vw - 256px)" : "calc(100vw - 60px)" }}
        >
          {pdfUrl ? (
            <>
              {/* CHAT PANE (flex-[4]) */}
              <div className="flex-[4] bg-white flex justify-center">
                <div className="chat-wrapper w-full max-w-full flex flex-col" style={{ height: windowHeight }}>
                  <div className="chat-list-items flex-grow-1 flex justify-center overflow-auto" ref={chatScrollRef}>
                    <div className="w-full max-w-[720px]">
                      <div className="px-4 py-2">
                        {loading && <div className="chat-message-item ai"><div className="chat-message"><Spin size="small" /></div></div>}
                        
                        {/* FIX 1: Changed item.message to item.content 
                        */}
                        {!loading && messages.map((item, index) => (
                          <div key={index} className={`chat-message-item ${item.role === 'user' ? 'human' : 'ai'}`}>
                            <div 
                              className="chat-message" 
                              dangerouslySetInnerHTML={{ 
                              __html: item.role === 'ai' 
                                ? `<div class="markdown-content">${getRenderedMessage(item.content)}</div>`
                                : item.content 
                            }}
                            ></div>
                          </div>
                        ))}
                        {sending && (
                          <div className="chat-message-item ai">
                            <div className="chat-message">
                              <Spin size="small" />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Chat Input */}
                  <div className={`chat-input-wrapper px-4 mt-auto`}>
                    <div
                      className={`send-container w-full max-w-[720px] mx-auto`}
                      onKeyDown={handleEnterKey}
                    >
                      <textarea
                        placeholder="Ask any question about this PDF..."
                        className="message-input"
                        rows={1}
                        ref={textAreaRef}
                        value={chatId ? followup : question}
                        onChange={(e) => chatId ? setFollowup(e.target.value) : setQuestion(e.target.value)}
                      ></textarea>
                      <button
                        className="message-send"
                        style={{ background: sending ? 'rgb(187 189 193)' : '#1677ff' }}
                        onClick={sendMessage}
                        disabled={sending}
                      >
                        <span className="flex flex-col items-center text-center text-[14px]">
                          <Send size={14} />
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* PDF PANE (flex-[6]) */}
              <div className="flex-[6] bg-white flex flex-col">
                <div className="pdf-reader h-full">
                  <PDFReader pdfUrl={pdfUrl} />
                </div>
              </div>
            </>
          ) : (
            /* FULL-WIDTH CHAT PANE (Welcome Screen) */
            <div className="flex-1 bg-white flex justify-center">
              <div className="chat-wrapper w-full max-w-full flex flex-col" style={{ height: windowHeight }}>
                
                <div className="chat-list-items flex-grow-1 flex justify-center overflow-auto" ref={chatScrollRef}>
                  <div className="w-full max-w-[720px]">
                    <div className="px-4 py-2">

                      {loading && <div className="chat-message-item ai"><div className="chat-message"><Spin size="small" /></div></div>}
                      
                      {/* FIX 2: Re-ordered this section to match chat.js
                        Welcome card shows *only* if overlay is true.
                      */}
                      {showOverlay && !loading && (
                        <div className="chat-message-item welcome">
                          <div className="chat-message" style={{ padding: '20px 30px', background: '#fff', borderRadius: '12px', maxWidth: '720px', margin: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                            <div className="flex justify-center items-center gap-4 mb-4">
                              <img src="/images/cividocs.png" alt="CiviBOT" style={{ height: '32px' }} />
                              <h1 style={{ fontSize: '24px', margin: 0 }}>
                                Your AI-Powered <span style={{ color: '#219c3bff' }}>PDF Assistant</span>
                              </h1>
                              <img src="/images/cividocs.png" alt="CiviBOT" style={{ height: '32px' }} />
                            </div>

                            <p style={{ color: '#555', marginBottom: '16px' }}>
                              Upload your document and get instant answers based on its content.
                            </p>

                            <h2 style={{ fontSize: '18px', marginBottom: '16px' }}>📌 How to Use:</h2>

                            <div style={{ textAlign: 'left', fontSize: '15px', lineHeight: '1.7' }}>
                              <p>📁 <strong>Upload a PDF –</strong> Click the button below to upload your document (max 50MB).</p>
                              <p>🎯 <strong>Be Specific –</strong> Ask clear and detailed questions about the document's content.</p>
                              <p>📚 <strong>Cite Sources –</strong> CiviBOT will provide page numbers from your PDF for its answers.</p>
                            </div>

                            <div style={{ marginTop: '30px', display: 'flex', gap: '20px', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
                              <input
                                type="file"
                                id="pdf-upload-input"
                                accept="application/pdf"
                                onChange={onChooseFile}
                                style={{ display: 'none' }}
                                disabled={sending}
                              />
                              <button
                                className="dropdown" 
                                style={{ 
                                  fontWeight: '600', 
                                  cursor: 'pointer', 
                                  border: '1px solid #1677ff', 
                                  color: '#1677ff', 
                                  background: '#e6f4ff'
                                }}
                                disabled={sending}
                                onClick={() => document.getElementById('pdf-upload-input').click()}
                              >
                                📁 Upload PDF
                              </button>
                              {file && <p style={{ marginTop: '10px', color: '#1677ff', textAlign: 'center', fontSize: '14px' }}>File: {file.name}</p>}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* FIX 2: Added the message list here. It was missing.
                        Changed item.message to item.content
                      */}
                      {!loading && messages.map((item, index) => (
                        <div key={index} className={`chat-message-item ${item.role === 'user' ? 'human' : 'ai'}`}>
                          <div 
                            className="chat-message" 
                            dangerouslySetInnerHTML={{ 
                            __html: item.role === 'ai' 
                              ? `<div class="markdown-content">${getRenderedMessage(item.content)}</div>`
                              : item.content
                          }}
                          ></div>
                        </div>
                      ))}

                      {/* FIX 2: Spinner now shows *after* messages, not conditionally.
                      */}
                      {sending && (
                        <div className="chat-message-item ai">
                          <div className="chat-message">
                            <Spin size="small" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Chat Input */}
                <div className={`chat-input-wrapper px-4 ${showOverlay ? "mt-6 mb-10 flex flex-col items-center" : "mt-auto"}`}>
                  {showOverlay && (
                    <div className="text-black text-[18px] font-semibold mb-2 text-center">
                      Ask your first question
                    </div>
                  )}

                  <div
                    className={`send-container w-full max-w-[720px] ${showOverlay ? "" : "mx-auto"}`}
                    onKeyDown={handleEnterKey}
                  >
                    <textarea
                      placeholder="Ask any question about this PDF..."
                      className="message-input"
                      rows={1}
                      ref={textAreaRef}
                      value={chatId ? followup : question}
                      onChange={(e) => chatId ? setFollowup(e.target.value) : setQuestion(e.target.value)}
                      disabled={sending && showOverlay} // Only disable on first upload
                    ></textarea>
                    <button
                      className="message-send"
                      style={{ background: sending ? 'rgb(187 189 193)' : '#1677ff' }}
                      onClick={sendMessage}
                      disabled={sending}
                    >
                      <span className="flex flex-col items-center text-center text-[14px]">
                        <Send size={14} />
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}